import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Send, Loader2, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';
import { patientLinkService } from '@/services/patientLinkService';
import { formatToLocalDate } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';

interface PatientPortalSectionProps {
  patientId: string;
  patientEmail?: string;
  patientPhone?: string;
}

interface PortalSession {
  id: string;
  created_at: string;
  expires_at: string;
  is_active: boolean;
  last_active_at?: string;
}

const PatientPortalSection = ({
  patientId,
  patientEmail,
  patientPhone,
}: PatientPortalSectionProps) => {
  const { clinic } = useClinic();
  const [isLoading, setIsLoading] = useState(false);
  const [sendingMethod, setSendingMethod] = useState<'email' | 'sms' | null>(null);
  const [sessions, setSessions] = useState<PortalSession[]>([]);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [sessionToRevoke, setSessionToRevoke] = useState<string | null>(null);

  // Fetch active portal sessions
  useEffect(() => {
    fetchSessions();
  }, [patientId]);

  const fetchSessions = async () => {
    setIsLoading(true);
    try {
      const response = await patientLinkService.getPatientLinks(clinic?.id, patientId, {
        purpose: 'patient_portal',
        status: 'active',
      });

      console.log('response', response);

      if (response?.success && response.data?.links) {
        // Get sessions from links
        const activeSessions: PortalSession[] = [];
        for (const link of response.data.links) {
          // if (link.sessions && link.sessions.length > 0) {
          //   activeSessions.push(...link.sessions.filter((s: PortalSession) => s.is_active));
          // }
          activeSessions.push(link);
        }
        setSessions(activeSessions);
      }
    } catch (error) {
      console.error('Error fetching portal sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInvite = async (method: 'email' | 'sms') => {
    setSendingMethod(method);
    try {
      const response = await patientLinkService.createPatientLink({
        patient_id: patientId,
        clinic_id: clinic?.id,
        purpose: 'patient_portal',
        expires_in_days: 30,
        otp_required: true,
        send_channel: method,
        send_to_contact: true,
      });

      if (response?.success) {
        toast({
          title: 'Portal Invite Sent',
          description: `A portal access link has been sent via ${method}.`,
          variant: 'success',
        });
        fetchSessions(); // Refresh sessions
      } else {
        throw new Error(response?.error?.message || 'Failed to send invite');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send portal invite',
        variant: 'destructive',
      });
    } finally {
      setSendingMethod(null);
    }
  };

  const handleRevokeSession = async () => {
    if (!sessionToRevoke) return;

    setIsRevoking(true);
    try {
      const response = await patientLinkService.revokePatientLink(
        clinic?.id || '',
        sessionToRevoke
      );
      if (response?.success) {
        toast({
          title: 'Session Revoked',
          description: 'The portal session has been revoked.',
          variant: 'success',
        });
        setSessions(prev => prev.filter(s => s.id !== sessionToRevoke));
      } else {
        throw new Error(response?.error?.message || 'Failed to revoke session');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to revoke session',
        variant: 'destructive',
      });
    } finally {
      setRevokeDialogOpen(false);
      setSessionToRevoke(null);
      setIsRevoking(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    try {
      // Revoke all active sessions

      await Promise.all(
        sessions.map(s => patientLinkService.revokePatientLink(clinic?.id || '', s.id))
      );

      toast({
        title: 'All Sessions Revoked',
        description: 'All portal sessions have been revoked.',
        variant: 'success',
      });
      fetchSessions(); // Refresh sessions list
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to revoke sessions',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div>
        <p className='mb-3 text-sm text-slate-600'>
          Send a secure sign-in link to give this patient portal access.
        </p>
        <div className='flex flex-wrap gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleSendInvite('email')}
            disabled={sendingMethod !== null || !patientEmail}
            className='text-slate-700'
          >
            {sendingMethod === 'email' ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Send className='mr-2 h-4 w-4' />
            )}
            Send via Email
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleSendInvite('sms')}
            disabled={sendingMethod !== null || !patientPhone}
            className='text-slate-700'
          >
            {sendingMethod === 'sms' ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Send className='mr-2 h-4 w-4' />
            )}
            Send via SMS
          </Button>
        </div>
        {!patientEmail && !patientPhone && (
          <p className='mt-2 flex items-center gap-1 text-xs text-amber-600'>
            <XCircle className='h-3 w-3' />
            Patient has no email or phone on file
          </p>
        )}
      </div>

        {/* Active Sessions */}
        {isLoading ? (
          <div className='flex items-center justify-center py-4'>
            <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
          </div>
        ) : sessions.length > 0 ? (
          <div>
            <div className='mb-2 flex items-center justify-between'>
              <p className='text-xs font-medium uppercase tracking-wide text-slate-500'>
                Active Sessions ({sessions.length})
              </p>
              {sessions.length > 1 && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleRevokeAllSessions}
                  className='h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700'
                >
                  <Trash2 className='mr-1 h-3 w-3' />
                  Revoke All
                </Button>
              )}
            </div>
            <div className='space-y-2'>
              {sessions.map(session => {
                const isExpired = new Date(session.expires_at) < new Date();
                return (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between rounded-lg p-3 ${
                      isExpired ? 'bg-slate-50' : 'bg-green-50'
                    }`}
                  >
                    <div className='flex items-center gap-2'>
                      {isExpired ? (
                        <XCircle className='h-4 w-4 text-slate-400' />
                      ) : (
                        <CheckCircle className='h-4 w-4 text-green-600' />
                      )}
                      <div>
                        <p className='text-sm font-medium text-slate-700'>
                          {isExpired ? 'Expired Session' : 'Active Session'}
                        </p>
                        <p className='flex items-center gap-1 text-xs text-slate-500'>
                          <Clock className='h-3 w-3' />
                          Expires {formatToLocalDate(session.expires_at, 'MMM DD, YYYY hh:mm A')}
                        </p>
                      </div>
                    </div>
                    {!isExpired && (
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => {
                          setSessionToRevoke(session.id);
                          setRevokeDialogOpen(true);
                        }}
                        className='h-7 text-red-600 hover:bg-red-50 hover:text-red-700'
                      >
                        <XCircle className='h-4 w-4' />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className='py-4 text-center text-sm text-slate-500'>No active portal sessions</div>
        )}

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Portal Session?</DialogTitle>
            <DialogDescription>
              This will immediately log the patient out of the portal. They will need to request a
              new sign-in link to regain access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setRevokeDialogOpen(false)}
              disabled={isRevoking}
            >
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleRevokeSession} disabled={isRevoking}>
              {isRevoking ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <Trash2 className='mr-2 h-4 w-4' />
              )}
              Revoke Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientPortalSection;
